import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCustomizeParams } from '@/features/experiences/components/CustomizeMode/useCustomizeParams';
import {
  SOURCE_REFRESH_COOLDOWN_MS,
  useRefreshSource,
} from '@/features/sources/hooks/useRefreshSource';
import { useRemoveFeed } from '@/features/sources/hooks/useRemoveFeed';
import { useRequestFeedAccess } from '@/features/sources/hooks/useRequestFeedAccess';
import {
  isFeedAccessible,
  isFeedOriginDeleted,
} from '@/features/sources/lib/feed.lib';
import { getOriginUrl } from '@/features/sources/lib/originLink.lib';
import { FeedOriginKind, type ExperienceFeed } from '@/types/gql.generated';
import {
  useDisclosure,
  useToast,
  Box,
  Button,
  Flex,
  Icon,
  Text,
  Tooltip,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import { getFeedTypeMeta } from './feedType';
import { FeedTypeIcon } from './FeedTypeIcon';
import { ShowHiddenToggle } from './ShowHiddenToggle';

type Props = {
  /** Whether the listed window holds any hidden occurrence. */
  hasHidden: boolean;
  feed: ExperienceFeed;
};

// luxon renders a just-completed sync as "in 0 seconds" / "0 seconds ago",
// which reads as broken next to a spinner that just stopped.
const toRelativeSyncedAt = (syncedAt: string): string | null => {
  const relative = DateTime.fromISO(syncedAt).toRelative();
  if (!relative) {
    return null;
  }
  return relative.includes('0 seconds') ? 'just now' : relative;
};

export const FeedDetailHeader = ({ hasHidden, feed }: Props) => {
  const { iconType, label } = getFeedTypeMeta(feed.kind);
  const { clearSource } = useCustomizeParams();
  const { removeFeed, isPending: isRemoving } = useRemoveFeed();
  const { refreshSource, isPending: isRefreshing } = useRefreshSource();
  const { requestFeedAccess, isPending: isRequestingAccess } =
    useRequestFeedAccess();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Client half of the refresh throttle. Starts on click rather than on
  // completion, matching the server window, which opens when the request
  // arrives. The server stays authoritative — this just avoids spending a
  // request it would reject.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (cooldownUntil <= now) {
      return;
    }
    const timer = setTimeout(() => setNow(Date.now()), cooldownUntil - now);
    return () => clearTimeout(timer);
  }, [cooldownUntil, now]);

  // Experience-backed feeds aren't calendars, so keep the remove copy neutral to
  // the kind: "experience" for linked Agenda Hero experiences, "calendar" otherwise.
  const isExperienceFeed = feed.kind === FeedOriginKind.Experience;
  const removeNoun = isExperienceFeed ? 'experience' : 'calendar';

  // Access to an experience-backed source can be taken away after it was added.
  // The source keeps its row and every setting on it, so the panel explains the
  // pause rather than the source appearing to have broken or vanished.
  const hasAccess = isFeedAccessible(feed);
  // A deleted origin is the one pause nobody can lift: there is no owner left to
  // grant access, and the server 404s on a request for it. Say so plainly and
  // point at the only action that helps, removing the source.
  const isOriginDeleted = isFeedOriginDeleted(feed);

  const isCoolingDown = cooldownUntil > now;
  const updatedAt = feed.syncedAt ? toRelativeSyncedAt(feed.syncedAt) : null;
  // Null for external sources, and for an origin that is neither published nor
  // one this editor collaborates on — there's nowhere to send them.
  const originUrl = getOriginUrl(feed);

  const handleRefresh = () => {
    setCooldownUntil(Date.now() + SOURCE_REFRESH_COOLDOWN_MS);
    refreshSource(feed.id, {
      onError: (error) => toast.error(getErrorMessage(error)),
    });
  };

  // Claims this source for the caller and re-checks. It comes back instantly if
  // they can already read the origin, otherwise the origin's owner is emailed.
  const handleRequestAccess = () =>
    requestFeedAccess(feed.id, {
      onSuccess: (data) => {
        toast.notify(
          data.requestFeedAccess.accessRevokedAt
            ? 'Request sent'
            : 'Access restored'
        );
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    });

  // Remove the feed from the schedule; deselect it once it's gone (the rail
  // refreshes from the invalidated experience query). The dialog stays open with
  // a loading state until success so errors can surface without losing context.
  const handleRemove = () =>
    removeFeed(feed.id, {
      onSuccess: () => {
        onClose();
        clearSource();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    });

  const refreshLabel = isCoolingDown
    ? 'Recently refreshed — try again shortly'
    : 'Refresh this calendar now';

  return (
    <>
      <Flex align="flex-start" gap={4} justify="space-between" mb={5}>
        <Box>
          <Text
            color="ink.strong"
            fontSize="29px"
            fontWeight={700}
            letterSpacing="-0.02em"
            lineHeight={1.1}
          >
            {feed.title}
          </Text>
          <Flex align="center" color="ink.soft" gap={1.5} mt={1.5}>
            <FeedTypeIcon iconType={iconType} />
            <Text color="ink.soft" fontSize="ah-status" fontWeight={600}>
              {label}
            </Text>
            {updatedAt && (
              <>
                <Text color="ink.muted" fontSize="ah-status">
                  /
                </Text>
                <Text color="ink.muted" fontSize="ah-status">
                  Updated {updatedAt}
                </Text>
              </>
            )}
          </Flex>
        </Box>

        <Flex align="center" flexShrink={0} gap={1}>
          <ShowHiddenToggle hasHidden={hasHidden} />
          {/* Experience-backed feeds have no external calendar to sync from. */}
          {!isExperienceFeed && hasAccess && (
            <Tooltip label={refreshLabel}>
              {/* Span keeps the tooltip reachable while the button is disabled. */}
              <Box as="span">
                <Button
                  aria-label={refreshLabel}
                  h="36px"
                  isDisabled={isCoolingDown}
                  isLoading={isRefreshing}
                  minW="36px"
                  px={0}
                  variant="ahGhost"
                  onClick={handleRefresh}
                >
                  <Icon icon="Refresh" size={4} />
                </Button>
              </Box>
            </Tooltip>
          )}
          {/* New tab: following a source shouldn't take the editor out of the
              schedule they're configuring. */}
          {originUrl && (
            <Tooltip label={`Open ${feed.title} in a new tab`}>
              <Button
                aria-label={`Open ${feed.title} in a new tab`}
                as="a"
                h="36px"
                href={originUrl}
                minW="36px"
                px={0}
                target="_blank"
                variant="ahGhost"
              >
                <Icon icon="ArrowUpRight" size={4} />
              </Button>
            </Tooltip>
          )}
          <Tooltip label={`Remove this ${removeNoun} from this schedule`}>
            <Button
              _hover={{ bg: 'destructive.50', color: 'destructive.500' }}
              aria-label={`Remove this ${removeNoun} from this schedule`}
              h="36px"
              minW="36px"
              px={0}
              variant="ahGhost"
              onClick={onOpen}
            >
              <Icon icon="Trash" size={4} />
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {!hasAccess && (
        <Box
          bg="orange.50"
          borderRadius="ah-md"
          mb={5}
          px={3}
          py={3}
          role="alert"
        >
          <Text color="ink.strong" fontSize="ah-status" fontWeight={600}>
            Events paused
          </Text>
          {isOriginDeleted ? (
            <Text color="ink.soft" fontSize="ah-status">
              {feed.originalTitle || feed.title} was deleted, so it has no more
              events to deliver. Remove it from this schedule when you are
              ready.
            </Text>
          ) : (
            <Text color="ink.soft" fontSize="ah-status">
              This schedule no longer has access to{' '}
              {feed.originalTitle || feed.title}. Your settings for it are saved
              and events will return as soon as access is restored.
            </Text>
          )}
          {!isOriginDeleted && (
            <Button
              isLoading={isRequestingAccess}
              mt={2}
              size="ah-sm"
              variant="ahPrimary"
              onClick={handleRequestAccess}
            >
              Request access
            </Button>
          )}
        </Box>
      )}

      {hasAccess && feed.error && (
        <Box
          bg="destructive.50"
          borderRadius="ah-md"
          mb={5}
          px={3}
          py={2}
          role="alert"
        >
          <Text color="destructive.500" fontSize="ah-status" fontWeight={600}>
            Last sync failed
          </Text>
          <Text color="ink.soft" fontSize="ah-status">
            {feed.error}
          </Text>
        </Box>
      )}

      <ConfirmDialog
        confirmButtonText="Remove"
        isLoading={isRemoving}
        isOpen={isOpen}
        message={`This ${removeNoun} will be removed from this schedule. The original ${removeNoun} won't be affected.`}
        title={`Remove ${feed.title}?`}
        onCancel={onClose}
        onConfirm={handleRemove}
      />
    </>
  );
};
